import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { Wifi, WifiOff, Settings, RefreshCw, Radio, Activity, X } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import espnowService, {
  type EspNowController,
  type EspNowRouter,
} from '../../services/espnowIrrigation';
import { valveAgeLabel, valveReport, valveSummary } from '../../utils/valveReport';
import { nodeState, nodeStateDot, nodeStateTitle } from '../../utils/nodeState';
import sensorDevicesService, { type SensorDevice } from '../../services/sensorDevices';
import orchardService from '../../services/orchard';
import TankLevelCard from '../../components/irrigation/TankLevelCard';
import GenericSensorCard from '../../components/irrigation/GenericSensorCard';
import MultiChannelSensorCard from '../../components/irrigation/MultiChannelSensorCard';
import { useOrgTimezone, formatTimeInTz, formatDateTimeInTz } from '@/utils/timezone';
import { useAppRefresh } from '../../hooks/useAppRefresh';
import { useModuleLicense } from '../../hooks/useModuleLicense';
import '../../styles/GlobalPageStyles.css';

/**
 * Unified Devices page — shows ESP-NOW controllers AND standalone sensor
 * devices in one place. Sensor devices that report via an ESP-NOW controller
 * (transport=espnow_peer) are shown inside that controller's Sensors tab, not
 * here, to avoid duplication.
 */
export default function EspNowDashboard() {
  const navigate = useNavigate();
  const orgTz = useOrgTimezone();
  const [searchParams, setSearchParams] = useSearchParams();
  const farmIdParam = searchParams.get('farm');
  const farmIdFilter = farmIdParam ? Number(farmIdParam) : null;

  const [controllers, setControllers] = useState<EspNowController[]>([]);
  const [routersByController, setRoutersByController] = useState<Record<string, EspNowRouter[]>>({});
  const [sensors, setSensors] = useState<SensorDevice[]>([]);
  const [farmName, setFarmName] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());

  // Matches the /irrigation/espnow/admin route gate and the backend registry:
  // the irrigation admin seat, not the org-admin RBAC role.
  const { hasAccess: canManageDevices } = useModuleLicense('irrigation', 'admin');

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const [ctrls, sensorList] = await Promise.all([
        espnowService.getControllers(),
        sensorDevicesService.listDevices().catch(() => [] as SensorDevice[]),
      ]);
      setControllers(ctrls);

      // Load routers for each controller in parallel
      const routerMap: Record<string, EspNowRouter[]> = {};
      await Promise.all(
        ctrls.map(async c => {
          try {
            routerMap[c.controller_id] = await espnowService.getRouters(c.controller_id);
          } catch {
            routerMap[c.controller_id] = [];
          }
        })
      );
      setRoutersByController(routerMap);

      // Standalone sensors only (those not attached to a listed controller)
      const controllerIds = new Set(ctrls.map(c => c.controller_id));
      setSensors(
        sensorList.filter(s => !s.field_controller_id || !controllerIds.has(s.field_controller_id))
      );

      setLastUpdated(new Date());
    } catch {
      setError('Failed to load devices');
    } finally {
      setLoading(false);
    }
  }, []);

  // Look up the farm name for the filter banner
  useEffect(() => {
    if (!farmIdFilter) { setFarmName(null); return; }
    let cancelled = false;
    orchardService.getFarms(1, 100)
      .then(res => {
        if (cancelled) return;
        if (!res.success) { setFarmName(null); return; }
        const farm = res.data?.farms?.find((f: { id: number; name: string }) => f.id === farmIdFilter);
        setFarmName(farm?.name ?? null);
      })
      .catch(() => { if (!cancelled) setFarmName(null); });
    return () => { cancelled = true; };
  }, [farmIdFilter]);

  // Apply farm filter
  const filteredControllers = useMemo(
    () => farmIdFilter == null ? controllers : controllers.filter(c => c.farm_id === farmIdFilter),
    [controllers, farmIdFilter]
  );
  const filteredSensors = useMemo(
    () => farmIdFilter == null ? sensors : sensors.filter(s => s.farm_id === farmIdFilter),
    [sensors, farmIdFilter]
  );

  function clearFarmFilter() {
    const next = new URLSearchParams(searchParams);
    next.delete('farm');
    setSearchParams(next, { replace: true });
  }

  useEffect(() => {
    load();
    const interval = setInterval(() => load(true), 30_000);
    return () => clearInterval(interval);
  }, [load]);

  useAppRefresh(useCallback(() => { load(true); }, [load]));

  function renderSensorCard(device: SensorDevice) {
    if (device.channels && device.channels.length > 0) {
      return <MultiChannelSensorCard key={device.device_id} device={device} />;
    }
    switch (device.type_key) {
      case 'tank_level':
        return <TankLevelCard key={device.device_id} device={device} />;
      default:
        return <GenericSensorCard key={device.device_id} device={device} />;
    }
  }

  const empty = !loading && filteredControllers.length === 0 && filteredSensors.length === 0;

  return (
    <>
      <PageHeader
        title="Devices"
        subtitle={farmIdFilter != null && farmName ? `Filtered by ${farmName}` : 'Controllers and sensors'}
        icon={<Radio className="w-6 h-6 text-blue-600" />}
        actionButtons={
          canManageDevices ? (
            <Link to="/irrigation/espnow/admin">
              <Button variant="outline" size="sm" aria-label="Manage Devices">
                <Settings className="w-4 h-4 sm:mr-1" />
                <span className="hidden sm:inline">Manage</span>
              </Button>
            </Link>
          ) : undefined
        }
      />
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="space-y-6">
          {farmIdFilter != null && (
            <div className="flex items-center gap-2 p-2.5 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-800">
              <span className="flex-1">
                Showing devices for{' '}
                <strong>{farmName ?? `farm #${farmIdFilter}`}</strong>
              </span>
              <button
                onClick={clearFarmFilter}
                className="flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900"
                aria-label="Show all devices"
              >
                <X className="w-3.5 h-3.5" />
                Show all
              </button>
            </div>
          )}

          {loading ? (
            <div className="flex justify-center items-center h-48 text-gray-400">
              <RefreshCw className="w-6 h-6 animate-spin mr-2" />
              Loading devices…
            </div>
          ) : error ? (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">{error}</div>
          ) : empty ? (
            <div className="flex flex-col items-center justify-center h-48 text-gray-400 gap-3">
              <Radio className="w-10 h-10 opacity-30" />
              <p className="text-sm">
                {farmIdFilter != null
                  ? 'No devices assigned to this farm yet.'
                  : 'No devices assigned to your organisation.'}
              </p>
              {farmIdFilter != null ? (
                <Button size="sm" variant="outline" onClick={clearFarmFilter}>
                  Show all devices
                </Button>
              ) : canManageDevices ? (
                <Link to="/irrigation/espnow/admin">
                  <Button size="sm">Add a Device</Button>
                </Link>
              ) : null}
            </div>
          ) : (
            <>
              {/* Controllers */}
              {filteredControllers.length > 0 && (
                <section className="space-y-4">
                  <h2 className="text-sm font-semibold text-gray-600 flex items-center gap-2">
                    <Radio className="w-4 h-4 text-blue-500" />
                    Controllers ({filteredControllers.length})
                  </h2>
                  {filteredControllers.map(ctrl => (
                    <ControllerCard
                      key={ctrl.controller_id}
                      controller={ctrl}
                      routers={routersByController[ctrl.controller_id] ?? []}
                      onConfigure={() => navigate(`/irrigation/espnow/${ctrl.controller_id}`)}
                    />
                  ))}
                </section>
              )}

              {/* Sensors */}
              {filteredSensors.length > 0 && (
                <section className="space-y-3">
                  <h2 className="text-sm font-semibold text-gray-600 flex items-center gap-2">
                    <Activity className="w-4 h-4 text-violet-500" />
                    Sensors ({filteredSensors.length})
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                    {filteredSensors.map(renderSensorCard)}
                  </div>
                </section>
              )}
            </>
          )}

          <p className="text-xs text-gray-400 text-right">
            Last updated {formatTimeInTz(lastUpdated.toISOString(), orgTz)} · Auto-refreshes every 30s
          </p>
        </div>
      </div>
    </>
  );
}

// ─── Controller Card ──────────────────────────────────────────────────────────

function ControllerCard({
  controller,
  routers,
  onConfigure,
}: {
  controller: EspNowController;
  routers: EspNowRouter[];
  onConfigure: () => void;
}) {
  const orgTz = useOrgTimezone();
  const onlineRouters = routers.filter(r => r.online).length;

  // Valves across the whole site, counted only where something has reported.
  // The nodes that have not are stated separately rather than folded into
  // "closed" — an unasked node is the reason a count can be short, and burying
  // that turns the headline into a guess.
  const valveTotals = routers.reduce(
    (acc, r) => {
      const report = valveReport(r);
      return report.reported
        ? { ...acc, open: acc.open + report.openCount, reported: acc.reported + 1 }
        : { ...acc, silent: acc.silent + 1 };
    },
    { open: 0, reported: 0, silent: 0 }
  );

  return (
    <Card className="overflow-hidden">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2">
              {controller.online ? (
                <Wifi className="w-4 h-4 text-green-500" />
              ) : (
                <WifiOff className="w-4 h-4 text-gray-400" />
              )}
              {controller.controller_name || controller.controller_id}
            </CardTitle>
            <div className="flex items-center gap-3 mt-1 text-xs text-gray-500 flex-wrap">
              <span className={`font-medium ${controller.online ? 'text-green-600' : 'text-gray-400'}`}>
                {controller.online ? 'Online' : 'Offline'}
              </span>
              {/* An online laptop with no radio is the case the site list used
                  to hide completely: "Online · 0 of 6 online" reads as a farm
                  where every node died, when the actual fault is one USB plug. */}
              {controller.online && controller.coordinator_connected === false && (
                <span
                  className="font-medium text-red-600"
                  title={controller.coordinator_error || 'The XBee coordinator is not connected'}
                >
                  No coordinator
                </span>
              )}
              {controller.ip && <span>{controller.ip}</span>}
              {controller.wifi_channel && <span>Ch {controller.wifi_channel}</span>}
              {/* XBee laptop sites report a semver; ESP-NOW masters report a
                  numeric firmware_version instead, so only one of these is
                  ever set on a given controller. */}
              {controller.bridge_version && <span>Bridge v{controller.bridge_version}</span>}
              {controller.last_seen && (
                <span>Last seen {formatDateTimeInTz(controller.last_seen, orgTz)}</span>
              )}
            </div>
          </div>
          <button
            onClick={onConfigure}
            aria-label="Configure"
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded-lg transition-colors flex-shrink-0"
          >
            <Settings className="w-3.5 h-3.5 sm:mr-0.5" />
            <span className="hidden sm:inline">Configure</span>
          </button>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        <div className="flex items-center flex-wrap gap-x-1.5 text-xs text-gray-500 mb-3">
          <Radio className="w-3.5 h-3.5" />
          <span>
            {routers.length} router{routers.length !== 1 ? 's' : ''} · {onlineRouters} online
          </span>
          {valveTotals.reported > 0 && (
            <span className={valveTotals.open > 0 ? 'text-blue-700 font-medium' : ''}>
              · {valveTotals.open > 0 ? `${valveTotals.open} valve${valveTotals.open === 1 ? '' : 's'} open` : 'no valves open'}
            </span>
          )}
          {valveTotals.silent > 0 && (
            <span
              className="text-amber-600"
              title="These nodes have never reported their outputs, so nothing here claims their valves are shut"
            >
              · {valveTotals.silent} not reporting valves
            </span>
          )}
        </div>

        {/* Router grid */}
        {routers.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 xl:grid-cols-6 gap-2">
            {routers.map(router => (
              <RouterChip key={router.router_mac} router={router} />
            ))}
          </div>
        ) : (
          <p className="text-xs text-gray-400 italic">No routers discovered yet.</p>
        )}
      </CardContent>
    </Card>
  );
}

// ─── Router Chip ──────────────────────────────────────────────────────────────

function RouterChip({
  router,
}: {
  router: EspNowRouter;
}) {
  const orgTz = useOrgTimezone();
  const state = nodeState(router);
  const valves = valveReport(router);
  const age = valveAgeLabel(valves);

  return (
    <div
      className={`rounded-xl border p-2.5 ${
        // Blue is reserved for water on the ground. Reachability is the dot's
        // job — tinting a card by it (and fading the offline ones) buried the
        // one thing this grid is for behind a state it does not describe.
        valves.openCount > 0 ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white'
      }`}
    >
      <div className="flex items-center gap-1 mb-1.5">
        <span
          className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${nodeStateDot(state)}`}
          title={nodeStateTitle(state)}
        />
        <span className="text-xs font-medium text-gray-700 truncate">
          {router.name || router.router_mac.slice(-6)}
        </span>
      </div>

      {/* One tile per relay the board carries. A tile is only drawn as shut
          when something has said so — see valveReport. */}
      <div className="grid grid-cols-4 gap-0.5">
        {valves.relays.map((on, i) => {
          const label = router.relay_labels?.[String(i)];
          const name = label || `Relay ${i}`;
          return (
            <div
              key={i}
              title={
                on === null
                  ? `${name} — state not reported`
                  : `${name} — ${on ? 'open' : 'closed'}${age ? ` (${age})` : ''}`
              }
              className={`rounded text-center text-xs py-0.5 ${
                on === null
                  ? 'bg-white text-amber-500 border border-dashed border-amber-300'
                  : on
                    ? 'bg-blue-500 text-white font-medium'
                    : 'bg-white text-gray-400 border border-gray-200'
              }`}
            >
              {label ? label.slice(0, 2) : i}
            </div>
          );
        })}
      </div>

      {/* The tiles alone are a colour code; this is the claim in words, with
          how old it is. A bitmask here is not a live feed — it is whatever the
          last command or check left behind. */}
      <div
        className={`text-[11px] mt-1 truncate ${
          !valves.reported ? 'text-amber-600' : valves.openCount > 0 ? 'text-blue-700 font-medium' : 'text-gray-500'
        }`}
        title={
          valves.reportedAtMs !== null
            ? `Reported ${formatDateTimeInTz(new Date(valves.reportedAtMs).toISOString(), orgTz)}`
            : 'Nothing has reported this node’s outputs — open a valve or run a node test to find out'
        }
      >
        {valveSummary(valves)}
        {age && <span className="text-gray-400 font-normal"> · {age}</span>}
      </div>

      {router.rssi_dbm !== null && (
        <div className="text-xs text-gray-400 text-right">{router.rssi_dbm} dBm</div>
      )}
    </div>
  );
}
